import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { Container, Card, Form, Button, Spinner, Alert, Row, Col, ListGroup } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';

// FirestoreのTimestampを 'YYYY-MM-DD' 形式の文字列に変換するヘルパー関数
const formatTimestampForInput = (timestamp) => {
  if (!timestamp) return '';
  // タイムゾーンを考慮して日本の日付を取得
  const date = timestamp.toDate();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// 曜日の日本語ラベルを定義
const dayLabels = {
  sunday: '日曜日',
  monday: '月曜日',
  tuesday: '火曜日',
  wednesday: '水曜日',
  thursday: '木曜日',
  friday: '金曜日',
  saturday: '土曜日',
};
const weekDaysInOrder = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

function HolidayAdmin() {
  const [weeklyHolidays, setWeeklyHolidays] = useState({
    sunday: false, monday: false, tuesday: false, wednesday: false, thursday: false, friday: false, saturday: false,
  });
  const [specialHolidays, setSpecialHolidays] = useState([]);
  const [newSpecialHoliday, setNewSpecialHoliday] = useState({ title: '', startDate: '', endDate: '' });

  const [publicHolidays, setPublicHolidays] = useState([]);
  const [selectedPublicHolidays, setSelectedPublicHolidays] = useState(new Set());

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const settingsDocRef = doc(db, 'holiday_settings', 'config');

  useEffect(() => {
    document.title = '休日管理 | My Order System';

    const fetchData = async () => {
      try {
        const docSnap = await getDoc(settingsDocRef);
        let existingHolidays = [];
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.weeklyHolidays) setWeeklyHolidays(data.weeklyHolidays);
          if (data.specialHolidays) {
            existingHolidays = data.specialHolidays.map(h => ({
              ...h,
              startDate: formatTimestampForInput(h.startDate),
              endDate: formatTimestampForInput(h.endDate),
            }));
            setSpecialHolidays(existingHolidays);
          }
        }

        const currentYear = new Date().getFullYear();
        const yearsToFetch = [currentYear, currentYear + 1];
        let allPublicHolidays = [];

        const holidayRequests = yearsToFetch.map(year =>
          axios.get(`https://date.nager.at/api/v3/PublicHolidays/${year}/JP`)
        );

        const results = await Promise.allSettled(holidayRequests);

        results.forEach(result => {
          if (result.status === 'fulfilled') {
            allPublicHolidays = [...allPublicHolidays, ...result.value.data];
          } else {
            console.warn('祝日データの取得に失敗しました:', result.reason.message);
          }
        });

        const uniqueHolidaysMap = new Map();
        allPublicHolidays.forEach(holiday => {
          if (!uniqueHolidaysMap.has(holiday.date)) {
            uniqueHolidaysMap.set(holiday.date, holiday);
          }
        });
        const uniquePublicHolidays = Array.from(uniqueHolidaysMap.values());

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const upcomingHolidays = uniquePublicHolidays
          .map(holiday => ({ date: holiday.date, name: holiday.localName }))
          .filter(holiday => new Date(holiday.date) >= today)
          .filter(holiday => !existingHolidays.some(h => h.startDate === holiday.date));

        setPublicHolidays(upcomingHolidays);

      } catch (err) {
        setError('設定の読み込みに失敗しました。');
        console.error(err);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const handleWeeklyChange = (e) => {
    const { name, checked } = e.target;
    setWeeklyHolidays(prev => ({ ...prev, [name]: checked }));
  };

  const handleSpecialChange = (e) => {
    const { name, value } = e.target;
    setNewSpecialHoliday(prev => ({ ...prev, [name]: value }));
  };

  const handleAddSpecialHoliday = () => {
    if (!newSpecialHoliday.title || !newSpecialHoliday.startDate || !newSpecialHoliday.endDate) {
      alert('すべてのフィールドを入力してください。');
      return;
    }
    if (new Date(newSpecialHoliday.startDate) > new Date(newSpecialHoliday.endDate)) {
      alert('開始日は終了日より前の日付を選択してください。');
      return;
    }
    const newHoliday = { ...newSpecialHoliday, id: uuidv4() };
    setSpecialHolidays(prev => [...prev, newHoliday].sort((a, b) => new Date(a.startDate) - new Date(b.startDate)));
    setNewSpecialHoliday({ title: '', startDate: '', endDate: '' });
  };

  const handleDeleteSpecialHoliday = (id) => {
    setSpecialHolidays(prev => prev.filter(h => h.id !== id));
  };

  const handlePublicHolidaySelect = (e, holidayDate) => {
    const newSelected = new Set(selectedPublicHolidays);
    if (e.target.checked) {
      newSelected.add(holidayDate);
    } else {
      newSelected.delete(holidayDate);
    }
    setSelectedPublicHolidays(newSelected);
  };

  const handleSelectAllPublicHolidays = (e) => {
    if (e.target.checked) {
      setSelectedPublicHolidays(new Set(publicHolidays.map(h => h.date)));
    } else {
      setSelectedPublicHolidays(new Set());
    }
  };

  const handleAddSelectedPublicHolidays = () => {
    if (selectedPublicHolidays.size === 0) {
      alert('追加する祝日を選択してください。');
      return;
    }
    const holidaysToAdd = publicHolidays
      .filter(h => selectedPublicHolidays.has(h.date))
      .map(h => ({
        id: uuidv4(),
        title: h.name,
        startDate: h.date,
        endDate: h.date,
      }));

    setSpecialHolidays(prev => [...prev, ...holidaysToAdd].sort((a, b) => new Date(a.startDate) - new Date(b.startDate)));
    setPublicHolidays(prev => prev.filter(ph => !selectedPublicHolidays.has(ph.date)));
    setSelectedPublicHolidays(new Set());
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const dataToSave = {
        weeklyHolidays,
        specialHolidays: specialHolidays.map(h => {
          // ★★★ タイムゾーン問題を回避するための修正 ★★★
          // 'YYYY-MM-DD'形式の文字列を分割し、数値を直接 new Date() に渡すことで、
          // 実行環境のローカルタイムゾーン（日本時間）の午前0時として日付オブジェクトを確実に生成する。
          const startParts = h.startDate.split('-').map(Number);
          const endParts = h.endDate.split('-').map(Number);
          
          // 月は0から始まるため、-1 する
          const startDate = new Date(startParts[0], startParts[1] - 1, startParts[2]);
          const endDate = new Date(endParts[0], endParts[1] - 1, endParts[2]);
          
          // 終了日はその日の終わり(23:59:59)までを範囲に含める
          endDate.setHours(23, 59, 59, 999); 
          
          return {
            ...h,
            startDate: Timestamp.fromDate(startDate),
            endDate: Timestamp.fromDate(endDate),
          };
        }),
      };
      await setDoc(settingsDocRef, dataToSave);
      setSuccess('休日設定を保存しました。');
    } catch (err) {
      setError('設定の保存に失敗しました。');
      console.error(err);
    }
    setSaving(false);
  };

  if (loading) {
    return <Container className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}><Spinner animation="border" /></Container>;
  }

  return (
    <main>
      <Container className="my-4">
        <Card className="shadow-sm">
          <Card.Header
            as="h2"
            className="p-3"
          >
            休日管理
          </Card.Header>
          <Card.Body className="p-4">
            {
              error &&
              <Alert variant="danger">
                {error}
              </Alert>
            }
            {
              success &&
              <Alert variant="success">
                {success}
              </Alert>
            }

            <section className="mb-4">
              <h5 className="mb-3">
                定休日設定
              </h5>
              <Form.Group>
                {weekDaysInOrder.map(day => (
                  <Form.Check
                    inline
                    key={day}
                    type="checkbox"
                    id={`weekly-${day}`}
                    label={dayLabels[day]}
                    name={day}
                    checked={weeklyHolidays[day]}
                    onChange={handleWeeklyChange}
                  />
                ))}
              </Form.Group>
            </section>
            <hr />
            <section className="mb-4">
              <h5 className="mb-3">
                日本の祝日から追加
              </h5>
              <Card className="bg-light">
                <Card.Body>
                  <Form.Check
                    type="checkbox"
                    id="select-all-public-holidays"
                    label={
                      <strong>
                        すべて選択/解除
                      </strong>
                    }
                    onChange={handleSelectAllPublicHolidays}
                    checked={
                      publicHolidays.length > 0
                      &&
                      selectedPublicHolidays.size === publicHolidays.length
                    }
                  />
                  <hr className="my-2" />
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {
                      publicHolidays.length > 0 ?
                        publicHolidays.map(holiday => (
                          <Form.Check
                            key={holiday.date}
                            type="checkbox"
                            id={`ph-${holiday.date}`}
                            label={`${holiday.date} (${holiday.name})`}
                            checked={selectedPublicHolidays.has(holiday.date)}
                            onChange={(e) => handlePublicHolidaySelect(e, holiday.date)}
                          />
                        ))
                        :
                        <p className="text-muted small mb-0">
                          追加可能な祝日はありません。
                        </p>
                    }
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="mt-3"
                    onClick={handleAddSelectedPublicHolidays}
                  >
                    選択した祝日 ({selectedPublicHolidays.size}件) を特別休日リストに追加
                  </Button>
                </Card.Body>
              </Card>
            </section>
            <hr />
            <section>
              <h5 className="mb-3">
                特別休日設定
              </h5>
              <Card className="mb-3">
                <Card.Header>
                  新規追加
                </Card.Header>
                <Card.Body>
                  <Row className="g-2 align-items-end">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>
                          タイトル
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="title"
                          value={newSpecialHoliday.title}
                          onChange={handleSpecialChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>
                          開始日
                        </Form.Label>
                        <Form.Control
                          type="date"
                          name="startDate"
                          value={newSpecialHoliday.startDate}
                          onChange={handleSpecialChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>
                          終了日
                        </Form.Label>
                        <Form.Control
                          type="date"
                          name="endDate"
                          value={newSpecialHoliday.endDate}
                          onChange={handleSpecialChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <div className="d-grid">
                        <Button onClick={handleAddSpecialHoliday}>
                          追加
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <h6>登録済みリスト</h6>
              <ListGroup>
                {specialHolidays.map(holiday => (
                  <ListGroup.Item
                    key={holiday.id}
                    className="d-flex justify-content-between align-items-center"
                  >
                    <div>
                      <strong>
                        {holiday.title}
                      </strong>
                      <br />
                      <small className="text-muted">
                        {holiday.startDate} ~ {holiday.endDate}
                      </small>
                    </div>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDeleteSpecialHoliday(holiday.id)}
                    >
                      削除
                    </Button>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </section>
            <div className="d-grid mt-4">
              <Button
                variant="primary"
                size="lg"
                onClick={handleSave}
                disabled={saving}
              >
                {
                  saving ?
                    <Spinner as="span" size="sm" />
                    :
                    '全ての休日設定を保存'
                }
              </Button>
            </div>
            <hr className="my-4" />
            <Link to="/admin">
              管理者メニューに戻る
            </Link>
          </Card.Body>
        </Card>
      </Container>
    </main>
  );
}

export default HolidayAdmin;
